<template>
	<layout-admin>
		<div class="flex justify-between items-center space-y-3 sm:p-6 lg:px-8">
			<h1 class="text-3xl font-semibold text-gray-900">
				{{ __('Subscription') }}: #{{ subscription.id }} von {{ subscription.user.name }}
			</h1>

			<ui-button
				class="bg-red-500 hover:bg-red-600"
				type="primary"
				:disabled="isDeleted"
				v-on:click="
									$bus.$emit('modal:open', {
										view: {
											view: 'views/Admin/SubscriptionDelete',
											data: { id: subscription.id }
										},
										size: 'sm'
									})
								"
				label="Subscription löschen"
			/>
		</div>

		<hr class="h-0.5 bg-gray-200 text-gray-200" />

		<div
			v-if="subscription.deleted_at"
			class="flex items-center gap-3 my-4 mx-6 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700"
		>
			<ui-icon name="alert-triangle" class="h-5 w-5 flex-shrink-0" stroke-width="2" />
			<span class="font-medium">Dieses Abo wurde gelöscht.</span>
		</div>

		<div class="p-6 space-y-3">
			<div class="py-2">
				<div class="flex items-center justify-between gap-x-2">
					<h2 class="text-2xl font-medium text-gray-700">Status: {{ __(subscription.status) }}</h2>
					<subscription-suspend :subscription="subscription" />
				</div>
				<h2 class="text-2xl font-medium text-gray-700 mb-4">Zahlung: {{ __(subscription.payment_status) }}</h2>

				<span class="block text-gray-600 leading-tight">User-ID: {{ subscription.user_id}}</span>

				<span class="block text-gray-600 leading-tight">Abonniert am: {{ $cropOptions.formatDate(subscription.created_at) }}</span>
				<span v-if="subscription.status === 'TRIAL' && subscription.trial_until"
							class="block text-gray-600 leading-tight">Testphase bis: {{ $cropOptions.formatDate(subscription.trial_until) }}</span>
				<span v-if="subscription.terminated_at"
							class="block text-gray-600 leading-tight">Gekündigt am: {{ $cropOptions.formatDate(subscription.terminated_at) }}</span>

			</div>

			<hr class="h-0.5 bg-gray-200 text-gray-200" />

			<div class="">
				<div class="flex items-center justify-between">
					<h2 class="text-xl font-medium text-gray-700 my-2">
						{{ __('Duration') }}: {{ $cropOptions.formatDate(subscription.valid_from) }} - {{ $cropOptions.formatDate(subscription.valid_until) }}
					</h2>

					<subscription-invoice-sent v-if="paymentMethod" :subscription="subscription" :paymentMethodCode="paymentMethod.code" />
				</div>

				<span v-if="subscription.billing_at"
							class="block text-gray-600 leading-tight flex items-center gap-x-2"
				>
					Nächste Zahlung: {{ $cropOptions.formatDate(subscription.billing_at) }}
				</span>

				<span class="block text-gray-600 leading-tight flex items-center gap-x-2">
					Zahlungsankündigung: {{ subscription.billing_reminder_at ? $cropOptions.formatDate(subscription.billing_reminder_at) : '-' }}
				</span>

				<span class="block text-gray-600 leading-tight flex items-center gap-x-2">
					Verlängerung aktiviert:
					<ui-icon
						v-if="subscription.recurring"
						class="w-4 h-4 text-green-400"
						:stroke-width="2"
						name="check-circle"
					/>

					<ui-icon
							v-else
							class="w-4 h-4 text-red-600"
							:stroke-width="2"
							name="alert-triangle"
						/>

				</span>

				<span v-if="subscription.invoice_sent_at"
							class="block text-gray-600 leading-tight flex items-center gap-x-2"
				>
					Rechnung versendet: {{ $cropOptions.formatDate(subscription.invoice_sent_at) }}
				</span>

			</div>

			<div class="py-3" v-if="paymentMethod">
				<h2 class="text-xl font-medium text-gray-700 mb-4">{{ __('Payment Method')}}</h2>
				<span class="flex items-center gap-x-2">
					<span class="block text-gray-600 leading-tight">{{ __(paymentMethod.label) }}</span>
					<img class="max-h-6" :src="paymentMethod.image" />
				</span>
				<div v-if="paymentMethodIsDirectDebit">
					<span class="block text-gray-600 leading-tight flex items-center gap-x-2">
						Wiederkehrende Zahlung:
						<ui-icon
							v-if="mollieSubscription?.status === 'active'"
							class="w-4 h-4 text-green-400"
							:stroke-width="2"
							name="check-circle"
						/>
						<ui-icon
							v-else
							class="w-4 h-4 text-red-600"
							:stroke-width="2"
							name="alert-triangle"
						/>
					</span>

					<h2 class="text-xl font-medium text-gray-700 my-2">
						{{ __('SepaMandate') }}
					</h2>

					<span class="block text-gray-600 leading-tight flex items-center gap-x-2">
						Gültig:
						<ui-icon
							v-if="sepaMandate?.status === 'valid'"
							class="w-4 h-4 text-green-400"
							:stroke-width="2"
							name="check-circle"
						/>
						<ui-icon
							v-else
							class="w-4 h-4 text-red-600"
							:stroke-width="2"
							name="alert-triangle"
						/>
					</span>


					<span class="block">Inhaber: {{ sepaMandate?.consumerName }}</span>
					<span class="block">IBAN: {{ sepaMandate?.consumerAccount }}</span>

				</div>
			</div>

			<div class="py-3" v-if="billingAddress">
				<h2 class="text-xl font-medium text-gray-700 mb-4">{{ __('Billing address')}}</h2>
				<span class="block text-gray-600 leading-tight">{{ billingAddress.company_name }}</span>
				<span class="block text-gray-600 leading-tight">{{ billingAddress.street }} {{ billingAddress.house_number }}</span>
				<span class="block text-gray-600 leading-tight">
					{{ billingAddress.postal_code }} {{ billingAddress.city }}
					<span v-if="billingAddress.country !== 'DE'">
						({{ billingAddress.country }})
					</span>
				</span>
				<span class="block text-gray-600 leading-tight mt-2">{{ billingAddress.email }}</span>
			</div>
		</div>

		<hr class="h-0.5 bg-gray-200 text-gray-200" />

		<div class="py-6">
			<div class="px-6 flex items-center justify-between">
				<h2 class="text-xl font-medium text-gray-700">{{ __('Payments')}}</h2>
					<ui-button
						class="flex items-center px-6 py-2"
						:disabled="!canCreatePayment"
						v-on:click="
									$bus.$emit('modal:open', {
										view: {
											view: 'views/Admin/PaymentCreate',
											data: { amount: 178.5, subscription_id: subscription.id }
										}
									})
								"
					>
						<ui-icon name="plus" class="h-4 w-4 mr-2" stroke-width="2" />
						{{ __("Zahlung erfassen") }}
					</ui-button>
			</div>
			<div v-if="payments" class="px-2">
				<admin-crop-grid
					:rows="payments"
					:cols="paymentColumns"
					:title-is-bold="false"
					defaultOrderBy="updated_at"
					:defaultOrderByAscending="false"
				/>
			</div>
		</div>



	</layout-admin>
</template>
<script>

// let options = require("../../cropOptions");
import SubscriptionInvoiceSent from './SubscriptionInvoiceSent.vue';
import SubscriptionSuspend from './SubscriptionSuspend.vue';

export default {
	components: {
		SubscriptionInvoiceSent,
		SubscriptionSuspend,
	},
	props: {
		user: {
			type: Object,
			required: true,
		},
		subscription: {
			type: Object,
			required: true,
		},
		subscriptionPaymentMethod: {
			type: Object,
			required: false,
		},
		paymentMethod: {
			type: Object,
			required: false,
		},
		payments: {
			type: Array,
			required: true,
		},
		mollieSubscription: {
			type: Object,
			required: false,
		},
		sepaMandate: {
			type: Object,
			required: false,
		},
		billingAddress: {
			type: Object,
			required: false,
		}
	},
	data() {
		return {
			paymentColumns: [
				{
					id: 'id',
					name: 'ID',
				},
				{
					id: 'amount',
					name: 'Betrag',
				},
				{
					id: 'status',
					name: 'Status',
				},
				{
					id: 'charged_back_at',
					name: 'Rückbuchung',
				},
				{
					id: 'status_reason',
					name: 'Begründung (Status)',
				},
				{
					id: 'payment_provider_payment_id',
					name: 'Zahlungs-Anbieter-Zahlungs-ID',
				},
				{
					id: 'payment_provider_subscription_id',
					name: 'Zahlungsanbieter-Abo-ID',
				},
				{
					id: 'created_at',
					name: 'Erstellt',
					render: (x, row) => this.$cropOptions.formatIsoStamp(row.created_at),
				},

				{
					id: 'updated_at',
					name: 'Aktualisiert',
					render: (x, row) => this.$cropOptions.formatIsoStamp(row.updated_at),
				},

			]
		};
	},
	computed: {
		canCreatePayment() {
				return this.subscription.invoice_sent_at && ['CHARGED_BACK', 'FAILED', 'DUE', 'PENDING'].includes(this.subscription.payment_status)
		},
		isDeleted() {
			return this.subscription.deleted_at !== null;
		},
		paymentMethodIsDirectDebit() {
			return this.paymentMethod?.code === 'directdebit';
		}
	}
}
</script>
